import { useEffect, useId, useState, type ReactNode } from "react";
import { ArrowUpDown, Filter, Search, X } from "lucide-react";

import { cn } from "@utils/cn";

type Panel = "filter" | "sort" | null;

interface TableViewControlsProps {
  filterContent?: ReactNode;
  sortContent?: ReactNode;
  activeFilterCount?: number;
  onClearFilters?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
  className?: string;
}

/**
 * Shared table controls that keep filtering and sorting in a focused right-side
 * panel instead of taking vertical space from every list view.
 */
export const TableViewControls = ({
  filterContent,
  sortContent,
  activeFilterCount = 0,
  onClearFilters,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search this table…",
  searchLabel = "Search table",
  className,
}: TableViewControlsProps) => {
  const [panel, setPanel] = useState<Panel>(null);
  const titleId = useId();
  const hasFilter = Boolean(filterContent);
  const hasSort = Boolean(sortContent);

  useEffect(() => {
    if (!panel) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPanel(null);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [panel]);

  const panelTitle = panel === "sort" ? "Sort" : "Filters";
  const panelContent = panel === "sort" ? sortContent : filterContent;

  return (
    <>
      <div className={cn("flex flex-wrap items-center justify-end gap-2", className)}>
        {onSearchChange && (
          <div className="relative mr-auto w-full sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" aria-hidden="true" />
            <input
              type="search"
              aria-label={searchLabel}
              value={searchValue ?? ""}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={searchPlaceholder}
              className="input h-9 w-full pl-9 pr-9"
            />
            {searchValue && (
              <button
                type="button"
                aria-label={`Clear ${searchLabel.toLowerCase()}`}
                onClick={() => onSearchChange("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-content-muted transition-colors hover:bg-surface-raised hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {hasFilter && (
          <button
            type="button"
            aria-label="Open filters"
            aria-pressed={panel === "filter"}
            onClick={() => setPanel((current) => (current === "filter" ? null : "filter"))}
            className="relative inline-flex h-9 items-center gap-2 rounded-md border border-border-default bg-surface-base px-3 text-sm font-medium text-content-secondary shadow-sm transition-colors hover:bg-surface-raised hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-default-dark dark:bg-surface-base-dark dark:text-content-secondary-dark dark:hover:bg-surface-raised-dark dark:hover:text-content-primary-dark"
          >
            <Filter className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Filter</span>
            {activeFilterCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        )}
        {hasSort && (
          <button
            type="button"
            aria-label="Open sort options"
            aria-pressed={panel === "sort"}
            onClick={() => setPanel((current) => (current === "sort" ? null : "sort"))}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-border-default bg-surface-base px-3 text-sm font-medium text-content-secondary shadow-sm transition-colors hover:bg-surface-raised hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-default-dark dark:bg-surface-base-dark dark:text-content-secondary-dark dark:hover:bg-surface-raised-dark dark:hover:text-content-primary-dark"
          >
            <ArrowUpDown className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Sort</span>
          </button>
        )}
      </div>

      {panel && (
        <div className="fixed inset-0 z-50" role="presentation">
          <button
            type="button"
            aria-label="Close table controls"
            className="absolute inset-0 cursor-default bg-black/30"
            onClick={() => setPanel(null)}
          />
          <aside
            aria-labelledby={titleId}
            aria-modal="true"
            role="dialog"
            className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-border-default bg-surface-base shadow-2xl dark:border-border-default-dark dark:bg-surface-base-dark"
          >
            <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4 dark:border-border-subtle-dark">
              <div>
                <h2 id={titleId} className="text-lg font-semibold text-content-primary dark:text-content-primary-dark">
                  {panelTitle}
                </h2>
                <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">
                  {panel === "sort" ? "Choose how rows are ordered." : "Refine the rows shown in this table."}
                </p>
              </div>
              <button
                type="button"
                aria-label={`Close ${panelTitle.toLowerCase()}`}
                onClick={() => setPanel(null)}
                className="rounded-md p-2 text-content-secondary transition-colors hover:bg-surface-raised hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-primary dark:text-content-secondary-dark dark:hover:bg-surface-raised-dark dark:hover:text-content-primary-dark"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">{panelContent}</div>
            {panel === "filter" && onClearFilters && (
              <div className="flex justify-between gap-3 border-t border-border-subtle px-5 py-4 dark:border-border-subtle-dark">
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="text-sm font-medium text-content-secondary hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-primary dark:text-content-secondary-dark dark:hover:text-content-primary-dark"
                >
                  Clear filters
                </button>
                <button
                  type="button"
                  onClick={() => setPanel(null)}
                  className="btn-primary px-4 py-2 text-sm"
                >
                  Done
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
};
